import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useAssistenciaTecnicaCadastroViewModel } from "../../viewModels/assistencia_tecnica_cadastro_view_model";
import { BackButton } from "../../../../../common/components/back_button";
import { DefaultFormSelectInput } from "../../../../../common/components/default_form_select_input";
import { DefaultFormAutocomplete } from "../../../../../common/components/default_form_autocomplete";
import { DefaultCheckBox } from "../../../../../common/components/default_checkbox";
import { DefaultFormDatePickerInput } from "../../../../../common/components/default_form_date_picker_input";
import { FormRadioGroupInput } from "../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { isValidId } from "../../../../../common/utils/utils";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { DefaultFormMoneyInput } from "../../../../../common/components/default_form_money_input";
import { AssistenciaTecnicaAtendimentoLista } from "../../../domain/models/assistencia_tecnica_atendimento_lista";
import { AssistenciaTecnicaPesquisaLista } from "../../../domain/models/assistencia_tecnica_pesquisa_lista";
import { AssistenciaTecnicaCategoriaLista } from "../../../domain/models/assistencia_tecnica_categoria_lista";
import { AssistenciaTecnicaArquivoLista } from "../../../domain/models/assistencia_tecnica_arquivo_lista";
import { useMemo } from "react";
import { RegistroAtendimentoModal } from "./components/registro_atendimento_modal";
import { PesquisaSatisfacaoModal } from "./components/pesquisa_satisfacao_modal";
import { ArquivoDiversoModal } from "./components/arquivo_diverso_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { PopperButton } from "../../../../../common/components/popper_button";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { PesquisaSatisfacaoClienteModal } from "./components/pesquisa_satisfacao_cliente_modal";

import {
  GenerateArquivosDiversosColumnDefinition,
  GenerateCategoriaColumnDefinition,
  GeneratePesquisaSatisfacaoColumnDefinition,
  GenerateRegistroAtendimentoColumnDefinition
} from "./columns";

import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import BottomBar from "../../../../../common/components/bottom_bar";
import CascateList from "../../../../../common/components/cascate_list";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * AssistenciaTecnicaCadastro
 */

export const AssistenciaTecnicaCadastro = () => {
  const viewModel = useAssistenciaTecnicaCadastroViewModel();

  const registroAtendimentoColumnDefinition = useMemo(() => {
    return GenerateRegistroAtendimentoColumnDefinition({
      onActionEditar: viewModel.handleRegistroAtendimentoOpenState,
      onActionDelete: viewModel.handleRegistroAtendimentoDeleteOpenState,
    });
  }, [
    viewModel.handleRegistroAtendimentoOpenState,
    viewModel.handleRegistroAtendimentoDeleteOpenState,
  ]);

  const pesquisaSatisfacaoColumnDefinition = useMemo(() => {
    return GeneratePesquisaSatisfacaoColumnDefinition({
      onActionEditar: viewModel.handlePesquisaSatisfacaoEditarOpenState,
      onActionDelete: viewModel.handlePesquisaSatisfacaoDeleteOpenState,
    });
  }, [
    viewModel.handlePesquisaSatisfacaoEditarOpenState,
    viewModel.handlePesquisaSatisfacaoDeleteOpenState,
  ]);

  const arquivosDiversosColumnDefinition = useMemo(() => {
    return GenerateArquivosDiversosColumnDefinition({
      onActionEditar: viewModel.handleArquivoDiversoOpenState,
      onActionDelete: viewModel.handleArquivoDiversoDeleteOpenState,
      onActionDownload: viewModel.handleArquivoDiversoDownload,
    });
  }, [
    viewModel.handleArquivoDiversoOpenState,
    viewModel.handleArquivoDiversoDeleteOpenState,
    viewModel.handleArquivoDiversoDownload,
  ]);

  const categoriaColumnDefinition = useMemo(() => {
    return GenerateCategoriaColumnDefinition({
      onActionCategoriaChange: viewModel.handleCategoriaChange,
    });
  }, [
    viewModel.handleCategoriaChange,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          leftSlot={
            <div className="flex flex-row items-center gap-6 bg-white px-5 py-2 rounded-lg border border-gray-200 shadow-sm ml-6 h-[50px]">
              <div className="flex flex-row items-center gap-3">
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider pt-0.5">
                  Status:
                </span>
                <div className="flex items-center mt-1.5">
                  <FormRadioGroupInput
                    name='statusAssistencia'
                    options={viewModel.statusAssistenciaOptions}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
              </div>

              <div className="w-px h-8 bg-gray-200"></div>

              <div className="flex flex-col justify-center">
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                  Cód. sistema
                </span>
                <span className="text-sm font-bold text-emerald-600 mt-0.5">
                  {viewModel.codigoSistema || '-'}
                </span>
              </div>

            </div>
          }

          rightSlot={
            <div className="flex flex-row gap-2">
              {
                viewModel.idAssistenciaTecnica &&
                <div className="w-[115px]">
                  <PopperButton
                    buttonLabel='Relatórios'
                    buttonColor='secondary'
                    options={viewModel.reportOptions}
                    onSelect={viewModel.handleReportPopperActions}
                  />
                </div>
              }
              {
                viewModel.idAssistenciaTecnica &&
                <div className="w-[115px]">
                  <PopperButton
                    buttonLabel='Opções'
                    buttonColor='yellow'
                    options={viewModel.buttonOptions}
                    onSelect={viewModel.handleOptionPopperActions}
                  />
                </div>
              }

              <BackButton
                onClick={viewModel.handleBack}
              />
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <form onSubmit={viewModel.handleAssistenciaTecnicaSubmit}>
        <div className="flex w-full gap-6 flex-col py-10 px-4 mb-8">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Entrega de Obras
            </Link>
            <Link underline="hover" color="inherit" component='span'>
              <LinkTo to='/AssistenciaTecnica_lista'>Assistência Técnica</LinkTo>
            </Link>
            <Typography className="text-primary-green">
              Assistência Técnica Cadastro
            </Typography>
          </Breadcrumbs>

          <div className="flex flex-col gap-0">
            <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
              <div className="flex gap-2 items-center w-full">
                <h1 className="text-2xl font-medium text-black dark:text-white">
                  Assistência Técnica Cadastro
                </h1>
                <RefreshButton onClick={viewModel.handleReload} />
              </div>

              <div className="w-full justify-start flex gap-2">
              </div>
            </div>
          </div>
          {
            viewModel.errorMessage !== undefined
              ? (
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.errorMessage}
                    onTryAgain={viewModel.handleReload} />
                </div>
              ) : (
                <>
                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                    <div className="w-full flex flex-col gap-4">
                      <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                        {/*
                        <DefaultFormInput
                          name='codigo'
                          label='Código'
                          readOnly={true}
                          reactHookForm={viewModel.hookForm}
                        />
                        */}

                        <DefaultFormInput
                          name='nroChamado'
                          label='Nº do Chamado'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormSelectInput
                          name='tipo'
                          label='Tipo'
                          showSelectionLabel={false}
                          options={viewModel.tipoOptions}
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormAutocomplete
                          name='centroCusto'
                          label='Centro de Custo'
                          options={viewModel.centroCustoDropdown}
                          getOptionKey={x => x.id}
                          getOptionLabel={x => x.title}
                          reactHookForm={viewModel.hookForm}
                          onChangeValue={e => viewModel.handleCentroCustoChange(e)}
                        />

                        <div>
                          <div className="w-full h-[2.85rem]">
                            {
                              viewModel.fillCliente ? (
                                <DefaultFormInput
                                  name='cliente'
                                  label='Cliente'
                                  reactHookForm={viewModel.hookForm}
                                />
                              ) : (
                                <DefaultFormAutocomplete
                                  name='idClienteCentroCusto'
                                  label='Cliente'
                                  options={viewModel.clientesDropdown}
                                  getOptionKey={x => x.idClienteCentroCusto}
                                  getOptionLabel={x => x.descricao ?? ''}
                                  reactHookForm={viewModel.hookForm}
                                />
                              )
                            }
                          </div>

                          <DefaultCheckBox
                            name='fillCliente'
                            label='Preencher o cliente'
                            value={viewModel.fillCliente}
                            onChangeValue={viewModel.handleFillClienteChange}
                          />
                        </div>

                        <DefaultFormInput
                          name='unidade'
                          label='Unidade'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormDatePickerInput
                          name='dataAbertura'
                          label='Data Abertura'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='contato'
                          label='Contato'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormDatePickerInput
                          name='previsaoRealizacaoVistoria'
                          label='Previsão Realização Vistoria'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormDatePickerInput
                          name='previsaoTerminoAssistencia'
                          label='Previsão Término Assistência'
                          reactHookForm={viewModel.hookForm}
                        />

                        <div className="w-full col-span-1 lg:col-span-2">
                          <FormRadioGroupInput
                            name='prioridade'
                            label='Prioridade'
                            options={viewModel.statusOptions}
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>
                      </div>

                      <DefaultFormInput
                        name='reclamacao'
                        label='Reclamação'
                        multiline={true}
                        rows={10}
                        reactHookForm={viewModel.hookForm}
                      />

                      <div className="w-full flex flex-col gap-4">
                        <div className="w-full flex flex-row justify-between items-center">
                          <span className="text-black dark:text-white font-bold text-lg">
                            Registro de Atendimento
                          </span>

                          <div className="w-[8rem]">
                            {
                              viewModel.idAssistenciaTecnica !== undefined &&
                              <PrimaryButton
                                buttonColor="primary-green"
                                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                                onMouseDown={e => {
                                  viewModel.handleRegistroAtendimentoOpenState(null, true);
                                  e.preventDefault();
                                }}>
                                Novo Item
                              </PrimaryButton>
                            }
                          </div>
                        </div>
                        <Stack spacing={0}>
                          <StyledDataGrid
                            tableName="assistencia_tecnica_cadastro_registro_atendimento"
                            disableColumnMenu={false}
                            autoHeight={true}
                            getRowId={(row: AssistenciaTecnicaAtendimentoLista) => row.idAssistenciaAtendimento}
                            columns={registroAtendimentoColumnDefinition}
                            loading={viewModel.loading}
                            disableRowSelectionOnClick={true}
                            rows={viewModel.registrosAtendimentos}
                            rowCount={viewModel.registrosAtendimentos.length}
                            pageSizeOptions={[10, 25, 50, 100]}
                            paginationMode="server"
                            getRowHeight={() => 'auto'}
                            paginationModel={{
                              page: viewModel.pagination.registroAtendimentos.page,
                              pageSize: viewModel.pagination.registroAtendimentos.rowsPerPage,
                            }}
                          />

                          <TablePagination
                            component="div"
                            count={viewModel.pagination.registroAtendimentos.totalItems}
                            page={viewModel.pagination.registroAtendimentos.page - 1}
                            onPageChange={(_, p) => viewModel.handleRegistroAtendimentosPageChange(p)}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            rowsPerPage={viewModel.pagination.registroAtendimentos.rowsPerPage}
                            onRowsPerPageChange={(e) => viewModel.handleRegistroAtendimentosRowsPerPageChange(parseInt(e.target.value))}
                          />
                        </Stack>
                      </div>

                      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
                        <div className="col-span-1 lg:col-span-2">
                          <FormRadioGroupInput
                            name='procedente'
                            label='Procedente'
                            options={viewModel.procedenteOptions}
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>

                        <div className="mb-2">
                          <DefaultFormDatePickerInput
                            name='dataReparo'
                            label='Data do Reparo'
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>

                        <div className="col-span-1 lg:col-span-2">
                          <div className="w-full flex flex-row gap-2 items-center">
                            <FormRadioGroupInput
                              name='taxa'
                              label='Taxa'
                              options={viewModel.taxaOptions}
                              reactHookForm={viewModel.hookForm}
                              onValueChange={viewModel.handleTaxaChange}
                            />

                            <div className="w-[5rem]">
                              <PrimaryButton
                                buttonColor="yellow"
                                onClick={() => viewModel.handleLimparTaxa()}>
                                Limpar
                              </PrimaryButton>
                            </div>
                          </div>
                        </div>

                        {
                          viewModel.taxa === 1 &&
                          <div className="w-full flex flex-col">
                            <FormRadioGroupInput
                              name='taxaCliente'
                              label='Taxa de visita'
                              options={viewModel.taxaOptions}
                              reactHookForm={viewModel.hookForm}
                              onValueChange={viewModel.handleTaxaClienteChange}
                            />

                            <span className="text-red-500">
                              A taxa de visita será cobrada do cliente?
                            </span>
                          </div>
                        }

                        <div className="col-span-1 lg:col-span-2">
                          <FormRadioGroupInput
                            name='resolvido'
                            label='Resolvido'
                            options={viewModel.resolvidoOptions}
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>

                        {
                          viewModel.taxaCliente === 1 &&
                          <DefaultFormMoneyInput
                            name='valorTaxa'
                            label='Valor Taxa'
                            reactHookForm={viewModel.hookForm}
                          />
                        }

                        <div className="mb-2">
                          <DefaultFormDatePickerInput
                            name='dataEncerramento'
                            label='Data Encerramento'
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>
                      </div>

                      <DefaultFormInput
                        name='observacoes'
                        label='Observações'
                        multiline={true}
                        rows={10}
                        reactHookForm={viewModel.hookForm}
                      />

                      <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        <DefaultFormMoneyInput
                          name='custo'
                          label='Custo'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>
                  </div>



                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                    <div className="w-full flex flex-col gap-4">
                      <div className="w-full flex flex-row justify-between items-center">
                        <span className="text-black dark:text-white font-bold text-lg">
                          Pesquisa de Satisfação
                        </span>

                        <div className="w-[8rem]">
                          {
                            viewModel.idAssistenciaTecnica !== undefined && viewModel.pesquisasSatisfacao.length < 1 &&
                            <PrimaryButton
                              buttonColor="primary-green"
                              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                              onMouseDown={e => {
                                viewModel.handlePesquisaSatisfacaoOpenState(true);
                                e.preventDefault();
                              }}>
                              Novo Item
                            </PrimaryButton>
                          }
                        </div>
                      </div>
                      <Stack spacing={0}>
                        <StyledDataGrid
                          tableName="assistencia_tecnica_cadastro_pesquisa_satisfacao"
                          disableColumnMenu={false}
                          autoHeight={true}
                          getRowId={(row: AssistenciaTecnicaPesquisaLista) => row.idPesquisaSatisfacaoCliente}
                          columns={pesquisaSatisfacaoColumnDefinition}
                          loading={viewModel.loading}
                          disableRowSelectionOnClick={true}
                          rows={viewModel.pesquisasSatisfacao}
                          rowCount={viewModel.pesquisasSatisfacao.length}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: viewModel.pagination.pesquisaSatisfacao.page,
                            pageSize: viewModel.pagination.pesquisaSatisfacao.rowsPerPage,
                          }}
                        />

                        <TablePagination
                          component="div"
                          count={viewModel.pagination.pesquisaSatisfacao.totalItems}
                          page={viewModel.pagination.pesquisaSatisfacao.page - 1}
                          onPageChange={(_, p) => viewModel.handlePesquisaSatisfacaoPageChange(p)}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          rowsPerPage={viewModel.pagination.pesquisaSatisfacao.rowsPerPage}
                          onRowsPerPageChange={(e) => viewModel.handlePesquisaSatisfacaoRowsPerPageChange(parseInt(e.target.value))}
                        />
                      </Stack>
                    </div>
                  </div>

                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                    <div className="w-full flex flex-col gap-4">
                      <div className="w-full flex flex-row justify-between items-center">
                        <span className="text-black dark:text-white font-bold text-lg">
                          Arquivos Diversos
                        </span>

                        <div className="w-[8rem]">
                          {
                            viewModel.idAssistenciaTecnica !== undefined &&
                            <PrimaryButton
                              buttonColor="primary-green"
                              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                              onMouseDown={e => {
                                viewModel.handleArquivoDiversoOpenState(null, true);
                                e.preventDefault();
                              }}>
                              Novo Item
                            </PrimaryButton>
                          }
                        </div>
                      </div>
                      <Stack spacing={0}>
                        <StyledDataGrid
                          tableName="assistencia_tecnica_cadastro_arquivos_diversos"
                          disableColumnMenu={false}
                          autoHeight={true}
                          getRowId={(row: AssistenciaTecnicaArquivoLista) => row.idAssistenciaArquivo}
                          columns={arquivosDiversosColumnDefinition}
                          loading={viewModel.loading}
                          disableRowSelectionOnClick={true}
                          rows={viewModel.arquivos}
                          rowCount={viewModel.arquivos.length}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: viewModel.pagination.arquivos.page,
                            pageSize: viewModel.pagination.arquivos.rowsPerPage,
                          }}
                        />

                        <TablePagination
                          component="div"
                          count={viewModel.pagination.arquivos.totalItems}
                          page={viewModel.pagination.arquivos.page - 1}
                          onPageChange={(_, p) => viewModel.handleArquivosPageChange(p)}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          rowsPerPage={viewModel.pagination.arquivos.rowsPerPage}
                          onRowsPerPageChange={(e) => viewModel.handleArquivosRowsPerPageChange(parseInt(e.target.value))}
                        />
                      </Stack>
                    </div>
                  </div>

                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                    <div className="w-full flex flex-col gap-4">
                      <div className="w-full flex flex-row justify-between items-center">
                        <span className="text-black dark:text-white font-bold text-lg">
                          Assinaturas
                        </span>

                        <div className="w-[8rem]">
                        </div>
                      </div>
                      <div className="w-full flex flex-row justify-around">
                        <div className="flex flex-col gap-2 items-center">
                          {
                            viewModel.assistenciaTecnica && viewModel.assistenciaTecnica.assinaturaCliente ? (
                              <img
                                src={`data:image/jpeg;base64,${viewModel.assistenciaTecnica.assinaturaCliente}`}
                                alt="Assinatura Cliente"
                                className="block w-[250px] h-[100px] bg-white"
                              />
                            ) : (
                              <div
                                className="block w-[250px] h-[100px] border border-[#C6C6C6] dark:border-border-dark-mode">
                              </div>
                            )
                          }
                          <span className="text-black dark:text-white">Cliente</span>
                        </div>


                        <div className="flex flex-col gap-2 items-center">
                          {
                            viewModel.assistenciaTecnica && viewModel.assistenciaTecnica.assinaturaConstrutora ? (
                              <img
                                src={`data:image/jpeg;base64,${viewModel.assistenciaTecnica.assinaturaConstrutora}`}
                                alt="Assinatura Construtora"
                                className="block w-[250px] h-[100px] bg-white"
                              />
                            ) : (
                              <div className="block w-[250px] h-[100px] border border-[#C6C6C6] dark:border-border-dark-mode rounded">
                              </div>
                            )
                          }
                          <span className="text-black dark:text-white">Construtora</span>
                        </div>
                      </div>

                      <CascateList title='Ação Corretiva (Não Usar esse campo)' textColor="text-red-500" defaultOpen={false}>
                        <>
                          <DefaultFormInput
                            name='acoesCorretivas'
                            label=''
                            multiline={true}
                            rows={10}
                            readOnly={true}
                            reactHookForm={viewModel.hookForm}
                          />
                        </>
                      </CascateList>

                      <Stack spacing={0}>
                        <StyledDataGrid
                          tableName="assistencia_tecnica_cadastro_categoria"
                          height="auto"
                          autoHeight={true}
                          disableColumnMenu={false}
                          getRowId={(row: AssistenciaTecnicaCategoriaLista) => row.idCategoriaAssistencia}
                          columns={categoriaColumnDefinition}
                          loading={viewModel.loading}
                          disableRowSelectionOnClick={true}
                          rows={viewModel.categorias}
                        />
                      </Stack>
                    </div>
                  </div>
                </>
              )
          }
        </div>


        {/**
          * BottomBar
          */}

        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isValidId(viewModel.idAssistenciaTecnica) &&
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => { viewModel.handleOpenChangeHistory(null, null); }}>
                  <span className="text-xs">
                    Historico de alterações
                  </span>
                </PrimaryButton>
              }
            </div>

            <div className="flex flex-row gap-4">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={viewModel.handleBack}>
                  Cancelar
                </h1>
              </div>
              <div className="w-[130px]">
                {viewModel.saving
                  ? (
                    <PrimaryButton>
                      Salvando...
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      type="submit"
                      startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                      Salvar
                    </PrimaryButton>
                  )}
              </div>
            </div>
          </div>
        </BottomBar>
      </form>

      {/**
        * Modals
        */}

      <RegistroAtendimentoModal
        open={viewModel.registroAtendimentoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        funcionariosResponsavelDropdown={viewModel.funcionariosResponsavelDropdown}
        selectedRegistroAtendimento={viewModel.selectedRegistroAtendimento}
        onClose={() => viewModel.handleRegistroAtendimentoOpenState(null, false)}
        onSubmit={viewModel.handleRegistroAtendimentoSubmit}
      />

      <PesquisaSatisfacaoModal
        open={viewModel.pesquisaSatisfacaoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        pesquisaSatisfacaoDropdown={viewModel.pesquisaSatisfacaoDropdown}
        onClose={() => viewModel.handlePesquisaSatisfacaoOpenState(false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoSubmit}
      />

      <ArquivoDiversoModal
        open={viewModel.arquivoDiversoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedArquivoDiverso={viewModel.selectedArquivoDiverso}
        onClose={() => viewModel.handleArquivoDiversoOpenState(null, false)}
        onSubmit={viewModel.handleArquivoDiversoSubmit}
        onDownload={viewModel.handleArquivoDiversoDownload}
      />

      <PesquisaSatisfacaoClienteModal
        open={viewModel.pesquisaSatisfacaoEditModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedPesquisaSatisfacaoCliente={viewModel.selectedPesquisaSatisfacao}
        itensPesquisaCliente={viewModel.itensPesquisaCliente}
        onClose={() => viewModel.handlePesquisaSatisfacaoEditarOpenState(null, false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoClienteEditarSubmit}
      />

      <DeleteDialog
        open={viewModel.registroAtendimentoDeleteModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handleRegistroAtendimentoDeleteOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleRegistroAtendimentoDeleteSubmit}
      />

      <DeleteDialog
        open={viewModel.pesquisaSatisfacaoDeleteModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handlePesquisaSatisfacaoDeleteOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handlePesquisaSatisfacaoDeleteSubmit}
      />

      <DeleteDialog
        open={viewModel.arquivoDiversoDeleteModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handleArquivoDiversoDeleteOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleArquivoDiversoDeleteSubmit}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />

    </>
  );
}